import React, { useMemo } from "react";
import { TagList } from "../../../ui";
import { formatDate } from "../../../lib/date_utils";
import { formatTagValue } from "../../tag-aliases/formatTagValue";
import { useTagAliases } from "../../tag-aliases/context";
import type { TraceItem } from "../types";
import { EvaluationCriteria } from "./EvaluationCriteria";

interface TraceOverviewProps {
  trace: TraceItem;
}

function isPassing(score: number | null): boolean {
  return score !== null && score >= 0.5;
}

/**
 * Presentational component that composes the overview section for a trace.
 * Combines metadata, evaluator criteria, scores, and tags in a card layout.
 */
export function TraceOverview({ trace }: TraceOverviewProps) {
  // Tags render by display name; `trace.tags` itself is untouched, and the raw
  // values remain in the JSON trace view for debugging and joins.
  const { aliases } = useTagAliases();
  const namedTags = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(trace.tags ?? {}).map(([key, value]) => [
          key,
          formatTagValue(String(value), aliases),
        ]),
      ),
    [trace.tags, aliases],
  );

  const evaluatorNames = [
    ...new Set(
      trace.evaluations
        .map((e) => e.evaluator_name)
        .filter((n): n is string => !!n)
    ),
  ];

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-5">
      <h3 className="text-base font-semibold text-foreground mb-4">Overview</h3>

      {/* Metadata */}
      <div className="space-y-2 mb-5">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-semibold text-muted-foreground min-w-[100px]">ID:</span>
          <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
            {trace.id}
          </code>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="font-semibold text-muted-foreground min-w-[100px]">
            Created At:
          </span>
          <span className="text-foreground">{formatDate(trace.created_at)}</span>
        </div>
      </div>

      {evaluatorNames.length > 0 && (
        <EvaluationCriteria criteria={evaluatorNames} />
      )}

      {/* Scores */}
      {trace.evaluations.length > 0 && (
        <div className="mb-4">
          <label className="text-sm font-semibold text-muted-foreground block mb-2">
            Scores
          </label>
          <div className="flex flex-wrap gap-2">
            {trace.evaluations.map((ev) => (
              <div
                key={ev.id}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted border border-border"
              >
                <span className="text-sm text-muted-foreground">
                  {ev.evaluator_name ?? "—"}
                </span>
                <span
                  className={`text-sm font-bold px-2 py-0.5 rounded ${
                    isPassing(ev.score)
                      ? "bg-success/15 text-success"
                      : "bg-destructive/15 text-destructive"
                  }`}
                >
                  {ev.score !== null ? ev.score.toFixed(2) : "—"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {trace.tags && Object.keys(trace.tags).length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-muted-foreground">Tags</span>
          <TagList tags={namedTags} />
        </div>
      )}
    </div>
  );
}
